import { UseQueryOptions, UseMutationOptions, useQuery } from "@tanstack/react-query";
import { FieldValues } from "react-hook-form";
import { PROFILES } from "./apiRoutes";
import axiosAuth from "../axiosAuth";

// Define the expected response type (adjust based on your API response)
interface Employee {
  id: string;
  name: string;
  email: string;
  mobile_number?: string;
  role: string[];
  city: string;
  state: string;
  geographical_region_id: string;
  description?: string;
  createdAt: string;
  isActive: boolean;
}

interface EmployeesResponse {
  data: {
    records: Employee[];
    pagination: { totalRecords: number }[];
  }[];
}

interface EmployeeResponse {
  data: Employee;
}

const fetchEmployees = async ({ data }: { data: FieldValues }) => {
  const response = await axiosAuth.get<EmployeesResponse>(PROFILES, {
    params: data,
  });
  return response.data;
};

const fetchEmployee = async (id: string) => {
  const response = await axiosAuth.get<EmployeeResponse>(`${PROFILES}${id}/`);
  return response.data;
};

// Query hook for employee list
const useEmployeesQuery = (
  data: FieldValues,
  queryParams?:
    | UseQueryOptions<EmployeesResponse, Error, EmployeesResponse, ["employees", FieldValues]>
    | UseMutationOptions<any, Error>
) =>
  useQuery<EmployeesResponse, Error, EmployeesResponse, ["employees", FieldValues]>({
    queryKey: ["employees", data],
    queryFn: () => fetchEmployees({ data }),
    ...(queryParams as any), // safely spread because TS can't know if it's query or mutation
  });

// Query hook for individual employee
export const useEmployeeQuery = (
  id: string,
  queryParams?: Omit<
    UseQueryOptions<
      EmployeeResponse,
      Error,
      EmployeeResponse,
      ["employee", string]
    >,
    "queryKey" | "queryFn"
  >
) =>
  useQuery<EmployeeResponse, Error, EmployeeResponse, ["employee", string]>({
    queryKey: ["employee", id],
    queryFn: () => fetchEmployee(id),
    enabled: !!id,
    ...(queryParams || {}),
  });

export { fetchEmployees };

export default useEmployeesQuery;
